import StoreScopeTag from "@/components/partner/StoreScopeTag";
import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Palette, Image as ImageIcon, Trash2, Store, Wand2 } from "lucide-react";
import { sharedApi } from "@/lib/api";
import { toast } from "sonner";

/**
 * Iter-288 — Store screen branding editor.
 *
 * Vendor-only self-service card that reads the vendor record via
 *   GET  /api/classifieds/engagement/vendors/me   → { branding: {...} }
 * and patches it via
 *   PATCH /api/classifieds/engagement/vendors/me/branding
 *
 * All /engagement/* calls send `X-App-Platform: nz_classified` per the
 * shared backend contract.
 *
 * Constraints (backend-enforced):
 *   • logo_base64   → data:image/* base64, ≤ ~500KB (client caps at 500_000
 *                    raw base64 chars ≈ 375KB decoded, safe margin)
 *   • brand_color   → strict #RRGGBB (422 otherwise)
 *   • tagline       → ≤ 80 chars
 *   • welcome_message → ≤ 240 chars
 *   • remove_logo   → true to explicitly clear an existing logo
 */

const ENGAGEMENT_HEADERS = { "X-App-Platform": "nz_classified" };
// Iter-288e — query-param fallback for header-stripping proxies.
const ENGAGEMENT_PARAM = { platform: "nz_classified" };
const MAX_LOGO_BYTES = 500 * 1024;
const HEX_RE = /^#[0-9a-fA-F]{6}$/;
const FALLBACK_COLOR = "#1F2937";

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ""));
    r.onerror = () => reject(r.error || new Error("read failed"));
    r.readAsDataURL(file);
  });
}

function initialsOf(name) {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || "").join("") || "?";
}

function contrastText(hex) {
  try {
    const h = (hex || "").replace("#", "");
    if (h.length !== 6) return "#ffffff";
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 160 ? "#0F172A" : "#ffffff";
  } catch { return "#ffffff"; }
}

export default function BrandingCard({ businessName }) {
  const qc = useQueryClient();

  const meQ = useQuery({
    queryKey: ["vendor-me"],
    retry: false,
    queryFn: async () => {
      const { data } = await sharedApi.get(
        "/classifieds/engagement/vendors/me",
        { headers: ENGAGEMENT_HEADERS, params: ENGAGEMENT_PARAM },
      );
      return data;
    },
  });

  // Vendor-only self-service card. 401/403/404 → hide entirely (the user
  // isn't a vendor). Any other error → show a retryable error state so we
  // never compute diffs against an unknown baseline.
  const errStatus = meQ.error?.response?.status;
  const notAVendor = meQ.isError && [401, 403, 404].includes(errStatus);
  const baselineFailed = meQ.isError && !notAVendor;

  const savedBranding = meQ.data?.branding || {};
  const [logo, setLogo] = useState(null); // null = untouched, "" = cleared, dataURL = new
  const [color, setColor] = useState("");
  const [tagline, setTagline] = useState("");
  const [welcome, setWelcome] = useState("");

  // Seed the form from the fetched record on first load and whenever the
  // server-side branding refreshes.
  useEffect(() => {
    if (!meQ.data) return;
    setColor(savedBranding.brand_color || "");
    setTagline(savedBranding.tagline || "");
    setWelcome(savedBranding.welcome_message || "");
    setLogo(null);
  }, [meQ.data, savedBranding.brand_color, savedBranding.tagline, savedBranding.welcome_message]);

  const previewLogo = logo === "" ? null : (logo || savedBranding.logo || null);
  const previewColor = HEX_RE.test(color) ? color : FALLBACK_COLOR;
  const previewText = useMemo(() => contrastText(previewColor), [previewColor]);
  const previewName = businessName || meQ.data?.business_name || "Your store";

  const saveMut = useMutation({
    mutationFn: async () => {
      const body = {};
      if (logo && logo !== "") body.logo_base64 = logo;
      if (logo === "") body.remove_logo = true;
      if (color && color !== (savedBranding.brand_color || "")) {
        if (!HEX_RE.test(color)) throw new Error("Brand colour must be a #RRGGBB hex.");
        body.brand_color = color;
      }
      if (tagline !== (savedBranding.tagline || "")) body.tagline = tagline;
      if (welcome !== (savedBranding.welcome_message || "")) body.welcome_message = welcome;

      if (Object.keys(body).length === 0) {
        throw new Error("Nothing to save yet — change a field first.");
      }

      const { data } = await sharedApi.patch(
        "/classifieds/engagement/vendors/me/branding",
        body,
        { headers: ENGAGEMENT_HEADERS, params: ENGAGEMENT_PARAM },
      );
      return data;
    },
    onSuccess: () => {
      toast.success("Store screen branding saved.");
      qc.invalidateQueries({ queryKey: ["vendor-me"] });
    },
    onError: (err) => {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : err?.message || "Couldn't save — please try again.");
    },
  });

  const onPickLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    if (f.size > MAX_LOGO_BYTES) {
      toast.error("Logo must be under 500KB — try a smaller image.");
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(f);
      setLogo(dataUrl);
    } catch {
      toast.error("Couldn't read that file. Please try another.");
    }
  };

  if (notAVendor) return null;

  return (
    <section
      data-testid="branding-card"
      className="mt-14 rounded-2xl bg-white shadow-card p-6 md:p-8"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] grid place-items-center">
          <Wand2 className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-display text-2xl font-black text-slate-900">Store screen branding</h2>
            <StoreScopeTag />
          </div>
          <p className="mt-1 text-sm text-slate-500">
            Customise the page customers see when they scan your till QR — logo, colour and welcome message.
          </p>
        </div>
      </div>

      {meQ.isLoading ? (
        <div className="py-10 grid place-items-center">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </div>
      ) : baselineFailed ? (
        <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-6 text-center" data-testid="branding-error">
          <div className="font-bold text-slate-900 text-sm">We couldn&apos;t load your branding right now.</div>
          <p className="mt-1 text-xs text-slate-600">Please try again in a moment. Your changes are safe.</p>
          <button
            type="button"
            onClick={() => meQ.refetch()}
            disabled={meQ.isFetching}
            data-testid="branding-error-retry"
            className="mt-3 inline-flex items-center gap-2 rounded-full bg-white hover:bg-slate-50 text-slate-800 border border-slate-200 text-xs font-bold px-4 py-1.5 shadow-sm transition-colors disabled:opacity-60"
          >
            {meQ.isFetching && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Try again
          </button>
        </div>
      ) : (
        <div className="mt-6 grid md:grid-cols-2 gap-6">
          {/* Editor */}
          <div className="space-y-4">
            <BrandingField label="Store logo" hint="Square PNG or JPG, ≤ 500KB">
              <div className="flex items-center gap-3">
                {previewLogo ? (
                  <img src={previewLogo} alt="" className="w-14 h-14 rounded-xl object-cover ring-1 ring-slate-200" />
                ) : (
                  <div className="w-14 h-14 rounded-xl bg-slate-100 text-slate-400 grid place-items-center">
                    <ImageIcon className="w-5 h-5" />
                  </div>
                )}
                <label className="inline-flex items-center gap-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold px-3 py-1.5 cursor-pointer">
                  Upload
                  <input
                    type="file"
                    accept="image/*"
                    onChange={onPickLogo}
                    className="hidden"
                    data-testid="branding-logo-input"
                  />
                </label>
                {(logo || savedBranding.logo) && logo !== "" && (
                  <button
                    type="button"
                    onClick={() => setLogo("")}
                    data-testid="branding-logo-remove"
                    className="inline-flex items-center gap-1 rounded-full bg-red-50 hover:bg-red-100 text-red-700 text-xs font-bold px-3 py-1.5"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Remove
                  </button>
                )}
              </div>
            </BrandingField>

            <BrandingField label="Brand colour" hint="#RRGGBB (e.g. #8E24AA)">
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  data-testid="branding-color-picker"
                  value={HEX_RE.test(color) ? color : FALLBACK_COLOR}
                  onChange={(e) => setColor(e.target.value)}
                  className="w-11 h-11 rounded-lg border border-soft cursor-pointer"
                />
                <input
                  type="text"
                  data-testid="branding-color-hex"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  placeholder="#8E24AA"
                  className="flex-1 rounded-xl border border-soft px-3 py-2 text-sm font-mono focus:outline-none focus:border-[color:var(--brand-blue)]"
                  maxLength={7}
                />
              </div>
            </BrandingField>

            <BrandingField label="Tagline" hint={`${tagline.length}/80`}>
              <input
                type="text"
                data-testid="branding-tagline"
                value={tagline}
                onChange={(e) => setTagline(e.target.value.slice(0, 80))}
                placeholder="Auckland's friendliest corner store"
                maxLength={80}
                className="w-full rounded-xl border border-soft px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--brand-blue)]"
              />
            </BrandingField>

            <BrandingField label="Welcome message" hint={`${welcome.length}/240`}>
              <textarea
                data-testid="branding-welcome"
                value={welcome}
                onChange={(e) => setWelcome(e.target.value.slice(0, 240))}
                placeholder="Kia ora! Scan, pay & save 10% today."
                maxLength={240}
                rows={3}
                className="w-full rounded-xl border border-soft px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--brand-blue)]"
              />
            </BrandingField>

            <button
              type="button"
              onClick={() => saveMut.mutate()}
              disabled={saveMut.isPending}
              data-testid="branding-save"
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold px-6 py-2.5 text-sm shadow-card transition-colors disabled:opacity-70"
            >
              {saveMut.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Save branding
            </button>
          </div>

          {/* Live preview */}
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">
              Live preview
            </div>
            <div
              data-testid="branding-preview"
              className="rounded-2xl overflow-hidden shadow-card ring-1 ring-slate-200"
            >
              <div
                style={{ backgroundColor: previewColor, color: previewText }}
                className="px-5 pt-5 pb-6"
              >
                <div className="flex items-center gap-3">
                  {previewLogo ? (
                    <img src={previewLogo} alt="" className="w-11 h-11 rounded-xl object-cover bg-white/10 ring-1 ring-white/20" />
                  ) : (
                    <div
                      className="w-11 h-11 rounded-xl grid place-items-center bg-white/15 ring-1 ring-white/25 font-display text-base font-black"
                      style={{ color: previewText }}
                    >
                      {initialsOf(previewName)}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-display text-lg font-black leading-tight truncate">{previewName}</div>
                    {tagline && <div className="text-[11px] opacity-85 truncate">{tagline}</div>}
                  </div>
                </div>
                {welcome && (
                  <div className="mt-4 text-sm font-semibold opacity-95 line-clamp-3">{welcome}</div>
                )}
                <div className="mt-4 inline-flex items-center gap-1 rounded-full bg-white/15 border border-white/25 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest">
                  <Palette className="w-3 h-3" /> Preview
                </div>
              </div>
              <div className="bg-white px-5 py-3 text-[10px] text-slate-400 text-center">
                Powered by <span className="font-bold text-slate-500">AllSale</span>
              </div>
            </div>
            <p className="mt-3 text-[11px] text-slate-400 flex items-center gap-1">
              <Store className="w-3 h-3" /> This is what customers see when they scan your till QR.
            </p>
          </div>
        </div>
      )}
    </section>
  );
}

function BrandingField({ label, hint, children }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">{label}</span>
        {hint && <span className="text-[10px] text-slate-400">{hint}</span>}
      </div>
      {children}
    </label>
  );
}
